"use client";

import { sendGTMEvent } from "@next/third-parties/google";
import { DEFAULT_COUNTRY, DEFAULT_CURRENCY, DEFAULT_LOCALE, DEFAULT_MEASUREMENT, storageUserCountry } from "@utils/default";
import { getCookie, setCookie } from "cookies-next";
import { OptionsType } from "cookies-next/lib/types";
import { Session } from "next-auth";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { redirect, useRouter } from "next/navigation";
import { usePathname } from "next/navigation";
import React, { Dispatch, FC, ReactNode, createContext, useCallback, useContext, useEffect, useReducer, useState } from "react";

import { LoadingContext } from "@/context/LoaderContext";
import { languageChangeTracking, settingsChangeTracking } from "@/dataLayer/dataLayer";
import useClientInfo from "@/hooks/useClientInfo";
import { UserSettings } from "@/types";

const recentSearchStorageKey = "recentSearch";
const storageCurrency = "currency";
const storageMeasurement = "measurement";
const storageLanguage = "language";
const storageCountry = "country";
const storageClientInfo = "clientInfo";

interface Props {
  children: ReactNode;
  settings: UserSettings;
}
interface UserProps {
  language: string;
  currency: "AED" | "USD" | "CNY" | "RUB" | "EUR" | "GBP" | "INR";
  measurement: "imperial" | "metric";
  changeLanguage: (userLanguage: string) => void;
  changeCurrencyAndMeasurement: (currency: string, measurement: string) => void;
  isLoggedIn: boolean;
  userSession: Session | undefined;
  updateLoginType: (type: string) => void;
  checkLoginError: (error: boolean) => void;
  loginType: string;
  loginError: boolean;
  loginSuccess: boolean;
  recentSearch: string[];
  setRecentSearch: Dispatch<string>;
  trendingSearch: string[];
  isResendOtpDisabled: boolean;
  updateIsResendOtpDisabled: (value: boolean) => void;
  fewResultsMaximum: number;
  isDialogueOpen: boolean;
  setIsDialogueOpen: (value: boolean) => void;
  // country: string | any;
  // phonePrefix: string;
  // updatePhonePrefix: (prefix: string) => void;
}

export const UserContext = createContext<UserProps>({
  language: "",
  currency: DEFAULT_CURRENCY,
  measurement: DEFAULT_MEASUREMENT,
  changeLanguage: () => {},
  changeCurrencyAndMeasurement: () => {},
  isLoggedIn: false,
  userSession: undefined,
  updateLoginType: () => {},
  checkLoginError: () => {},
  loginType: "",
  loginError: false,
  loginSuccess: false,
  recentSearch: [],
  setRecentSearch: () => {},
  trendingSearch: [],
  isResendOtpDisabled: false,
  updateIsResendOtpDisabled: () => {},
  fewResultsMaximum: 0,
  isDialogueOpen: false,
  setIsDialogueOpen: () => {},
  // country: "",
  // phonePrefix: "",
  // updatePhonePrefix: () => {},
});

const recentSearchReducer = (currentState: string[], newParam: string): string[] => {
  const categories = newParam.split(",");
  const recentSearch = [...new Set([...currentState, ...categories])].filter((item) => item !== "");
  setCookie(recentSearchStorageKey, recentSearch.join(","), cookieOptions);
  return recentSearch;
};

const initializeRecentSearch = () => {
  const existingValue = getCookie(recentSearchStorageKey) ?? "";
  const categories = existingValue.split(",");
  const recentSearch = [...new Set([...categories])].filter((item) => item !== "");
  return recentSearch;
};

const CookieExpirationDate = new Date();
CookieExpirationDate.setFullYear(CookieExpirationDate.getFullYear() + 10);
const cookieOptions: OptionsType = { expires: CookieExpirationDate, sameSite: "lax" };
const UserProvider: FC<Props> = ({ children, settings }) => {
  const { loading } = useContext(LoadingContext);
  const pathname = usePathname();
  const router = useRouter();
  const { data: session, status } = useSession();

  const userStorageLanguage = getCookie(storageLanguage)?.toLowerCase();
  const [language, setLanguage] = useState(userStorageLanguage || settings?.language);
  const [currency, setCurrency] = useState(settings.currency);
  const [measurement, setMeasurement] = useState(settings.measurement);
  const [country, setCountry] = useState(settings.country);
  // const [phonePrefix, setPhonePrefix] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [userSession, setUserSession] = useState<Session>();
  const [loginType, setLoginType] = useState("");
  const client = useClientInfo({});
  const [isResendOtpDisabled, setIsResendOtpDisabled] = useState(true);
  const [isDialogueOpen, setIsDialogueOpen] = useState<boolean>(false);

  /**
   * Search settings
   */
  const [recentSearch, setRecentSearch] = useReducer(recentSearchReducer, settings?.recentSearch, initializeRecentSearch);
  const [trendingSearch] = useState<string[]>(settings?.trendingSearch ?? []);
  const [fewResultsMaximum] = useState<number>(settings?.fewResultsMaximum ?? 5);
  const t = useTranslations();

  useEffect(() => {
    if (language) {
      setCookie(storageLanguage, language, cookieOptions);
    }
  }, [language]);

  useEffect(() => {
    if (currency) {
      setCookie(storageCurrency, currency, cookieOptions);
    }
  }, [currency]);
  useEffect(() => {
    if (country) {
      setCookie(storageCountry, country, cookieOptions);
    }
  }, [country]);
  useEffect(() => {
    if (measurement) {
      setCookie(storageMeasurement, measurement, cookieOptions);
    }
  }, [measurement]);

  const updateIsResendOtpDisabled = useCallback((value: boolean) => {
    setIsResendOtpDisabled(value);
  }, []);

  useEffect(() => {
    if (session) {
      setIsLoggedIn(true);
      setUserSession(userSession);
      if (pathname.includes("login")) {
        router.push("/my-profile");
      }
    }
    if (status !== "loading" && session === null && pathname.includes("my-profile")) {
      router.push("/login");
    }
  }, [pathname, router, session, status, setUserSession, userSession]);

  const changeLanguage = useCallback(
    (userLanguage: string) => {
      //console.log("changeLanguage >>>>>>>>>>>>>>", userLanguage);
      if (userLanguage && country && pathname) {
        const localePattern = "/([a-z]{2}-[a-z]{2})/(.*)?";
        const localeRegexp = new RegExp(localePattern);
        const newURLPathGroups = pathname?.match(localeRegexp);
        const newURL = `/${userLanguage}-${country}/${newURLPathGroups?.[2] ?? ""}`;
        if (newURL !== pathname) {
          loading(true);
          const [language, country] = newURLPathGroups?.[1].split("-")!;
          setCookie(storageLanguage, userLanguage, cookieOptions);
          setCookie(storageCountry, country, cookieOptions);
          setLanguage(userLanguage);
          languageChangeTracking(userLanguage);
          router.replace(newURL);
          router.refresh();
        }
      }
    },
    [country, pathname, router],
  );

  // Detect route change completion and remove loading
  // Using this when language is changed then removed the Loading
  useEffect(() => {
    loading(false);
  }, [pathname]);

  // const updatePhonePrefix = useCallback((prefix: string) => {
  //   setPhonePrefix(prefix);
  // }, []);

  const checkLoginError = useCallback((isError: boolean) => {
    setLoginError(isError);
    setLoginSuccess(!isError);
    setTimeout(() => {
      setLoginError(false);
      setLoginSuccess(false);
    }, 5000);
  }, []);

  const changeCurrencyAndMeasurement = useCallback(
    (currency: any, measurement: any) => {
      setCurrency(currency);
      setMeasurement(measurement);
      settingsChangeTracking(currency, measurement);
      router.refresh();
    },
    [router],
  );

  const updateLoginType = useCallback((type: string) => {
    setCookie("loginType", type);
  }, []);

  useEffect(() => {
    if (client && !!typeof window) {
      const cookie = getCookie(storageClientInfo);
      const cookieValue = !!cookie ? JSON.parse(cookie as string) : {};
      const info = client.getResult();
      const newCookieValue = {
        ...cookieValue,
        ...{ browser: info.browser.name, os: info.os.name, resolution: `${window.screen.availWidth}x${window.screen.availHeight}` },
      };
      setCookie("clientInfo", JSON.stringify(newCookieValue), cookieOptions);
      const aid = new URLSearchParams(location.search).get("aid");
      const maxDay = Number(t("aid.expires.day")) || 20;
      if (aid) {
        setCookie("aid", aid, { maxAge: maxDay * 24 * 60 * 60 });
        sendGTMEvent({ event: "agent_id_set", value: aid });
      }
    }
  }, [client]);

  // Map country codes to their respective currencies
  const countryLanguageMap: any = {
    AE: { locale: "en-ae", currency: "AED" },
    US: { locale: "en-us", currency: "USD" },
    CN: { locale: "en-cn", currency: "CNY" },
    RU: { locale: "en-ru", currency: "RUB" },
    GB: { locale: "en-gb", currency: "GBP" },
    IN: { locale: "en-in", currency: "INR" },
    CA: { locale: "en-ca", currency: "USD" },
    AU: { locale: "en-au", currency: "USD" },
    SG: { locale: "en-sg", currency: "USD" },
    TR: { locale: "en-tr", currency: "USD" },
    IQ: { locale: "en-iq", currency: "USD" },
    IT: { locale: "en-it", currency: "EUR" },
    DE: { locale: "en-de", currency: "EUR" },
    FR: { locale: "en-fr", currency: "EUR" },
  };
  const cookieUserCountry = getCookie(storageUserCountry)?.toUpperCase() || DEFAULT_COUNTRY;
  // Set currency based on the country state
  useEffect(() => {
    if (country) {
      const localeCurrency = countryLanguageMap[cookieUserCountry] || { locale: DEFAULT_LOCALE, currency: DEFAULT_CURRENCY };
      const savedCurrency = getCookie(storageCurrency); // Check if user has manually selected a currency
      //console.log("cookieUserCountry", cookieUserCountry, localeCurrency);
      setCurrency(localeCurrency.currency || savedCurrency); // Use saved currency if available, else use default
      setCookie(storageCurrency, savedCurrency || localeCurrency.currency, cookieOptions);
    }
  }, []);
  /*
    End => Update User Currency
  */

  return (
    <UserContext.Provider
      value={{
        language,
        currency,
        measurement,
        changeLanguage,
        changeCurrencyAndMeasurement,
        isLoggedIn,
        userSession,
        updateLoginType,
        loginType,
        checkLoginError,
        loginError,
        loginSuccess,
        recentSearch,
        setRecentSearch,
        trendingSearch,
        isResendOtpDisabled,
        updateIsResendOtpDisabled,
        fewResultsMaximum,
        isDialogueOpen,
        setIsDialogueOpen,
        // country,
        // phonePrefix,
        // updatePhonePrefix,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
export default UserProvider;
